import { useEffect } from "react";
import { useUser } from "@/features/user";
import { useError } from "@/providers/Error";
import { emailMessages, genericMessages } from "@/constants/errorMessages";
import { isEmailValid, normalizeEmail } from "@/lib/emailUtils";

// Saves the email for the email page and the chat email step. Saving it
// sends the verification code.
export const useSubmitEmail = ({
  onError,
}: { onError?: (message: string) => void } = {}) => {
  const { setError } = useError();
  // The chat answers errors in the conversation instead of the popover.
  const reportError = onError ?? setError;
  const { updatePersonalData, isUpdatePending, updatePersonalDataError } =
    useUser();

  useEffect(() => {
    if (updatePersonalDataError) {
      reportError(genericMessages.unexpected);
    }
  }, [updatePersonalDataError]);

  // Resolves true once the email is saved.
  const submitEmail = async (email: string | undefined) => {
    if (!isEmailValid(email)) {
      reportError(emailMessages.invalid_email);
      return false;
    }

    let result;
    try {
      result = await updatePersonalData({ email: normalizeEmail(email) });
    } catch {
      // Reported by the mutation error effect.
      return false;
    }

    if (!result.isSuccess) {
      reportError(
        emailMessages[result.emailError as keyof typeof emailMessages] ??
          genericMessages.unexpected,
      );
      return false;
    }

    return true;
  };

  return { submitEmail, isLoading: isUpdatePending };
};
